import { createSlice, createAsyncThunk, nanoid } from "@reduxjs/toolkit";
import docApi from "../api/DocApi";
import {
  AddMemoReq,
  DocAIChatRequest,
  DocDetailDto,
  DocDownloadRequestDTO,
  DocInfo,
  DocumentListReq,
  DocumentListResp,
  FileFolderReq,
  FolderInfoDTO,
  FwocFilesLicensesRequestDTO,
  MemoDTO,
  ReadReqeustDto,
  ReadRequestDto,
  RequestRespond,
  UploadCompleteRequestDTO,
  UploadFile,
  UploadProgress,
  WrapsodySetRevisionMemoResult,
} from "../../types/files";
import dayjs from "dayjs";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import FileSaver from "file-saver";
import { FileWithPath } from "react-dropzone/.";
import { DeliveryUIDRequestRespondDTO } from "../../types/adminSetting";

interface DocumentState {
  contentAsideDetail?: DocDetailDto;
  contentDetail?: DocDetailDto;
  docListInfo?: DocumentListResp;
  docList?: DocInfo[];
  dragItem?: DocInfo[];
  dragDirectly?: boolean;
  uid?: string;
  memos?: WrapsodySetRevisionMemoResult;
  requestReadList?: RequestRespond[];
  uploadDocsProgress: UploadProgress;
  folderList?: FolderInfoDTO[];
  recallNum: number;
  savedContentThumbnail: Record<string, string>;
}
// # initial state
const initialState: DocumentState = {
  contentAsideDetail: undefined,
  contentDetail: undefined,
  docListInfo: undefined,
  docList: undefined,
  uid: undefined,
  memos: undefined,
  dragDirectly: false,
  requestReadList: undefined,
  recallNum: 0,
  uploadDocsProgress: {
    totalCount: 0,
    totalSize: 0,
    successCount: 0,
    failCount: 0,
    progress: 0,
    fileName: "",
    uploadFiles: [],
  },
  savedContentThumbnail: {},
};
export const trunkGetContentDetail = createAsyncThunk(
  "content/detail",
  async (req: { workgroupId: string; syncId: string; type?: "aside" }) => {
    const resp = await docApi.getContentDetail(req);
    return resp;
  }
);
export const trunkGetContentThumbnail = createAsyncThunk(
  "content/thumnail",
  async (req: { syncId: string; revisionNo: number }) => {
    const resp = await docApi.getContentThumbnail(req);
    return resp;
  }
);
export const trunkSaveContentThumbnail = createAsyncThunk(
  "content/thumnail/set",
  async (req: { syncId: string; revisionNo: number; blob: string }) => {
    return req;
  }
);
export const trunkGetContentList = createAsyncThunk(
  "content/list",
  async (req: DocumentListReq) => {
    const resp = await docApi.getContentList(req);
    return resp;
  }
);
export const trunkCheckMyBoxFileExist = createAsyncThunk(
  "content/myBoxTotallist",
  async () => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);
    const myId = loginDto?.userInfo?.userId ?? "";

    const resp = await Promise.all([
      docApi.getContentList({
        page: 0,
        size: 1,
        order: "datedesc",
        search: "",
        readType: "",
        masterUserId: "-1",
        tagCodes: ["NONEWORKGROUP"],
        date: {
          start: 0,
          end: 0,
        },
        folderCode: "",
      }),
      docApi.getContentList({
        page: 0,
        size: 1,
        order: "datedesc",
        search: "",
        readType: "",
        masterUserId: myId,
        tagCodes: ["NONEWORKGROUP"],
        date: {
          start: 0,
          end: 0,
        },
        folderCode: "",
      }),
    ]);
    return resp;
  }
);
export const trunkGetReadRequestContentList = createAsyncThunk(
  "content/list/readRequest",
  async (req: ReadRequestDto) => {
    const resp = await docApi.getReadRequestContentList(req);
    return resp;
  }
);
export const trunkGetContentFavorite = createAsyncThunk(
  "content/favorite",
  async (req: { flag: boolean; syncIds: string[]; page?: string }) => {
    const resp = await docApi.getContentFavorite(req);
    return resp;
  }
);
export const trunkAddContentMemo = createAsyncThunk(
  "content/memo/add",
  async (req: AddMemoReq) => {
    const resp = await docApi.addContentMemo(req);
    return resp;
  }
);
export const trunkGetDocUsingByWebIncludeToken = createAsyncThunk(
  "content/webusing",
  async (req: FwocFilesLicensesRequestDTO) => {
    const resp = await docApi.getDocUsingByWebIncludeToken(req);
    return resp;
  }
);
export const trunkGetContentDownload = createAsyncThunk(
  "content/docDownload",
  async (req: DocDownloadRequestDTO) => {
    return await docApi.docDownload(req);
  }
);
export const trunkDocDecryptDownload = createAsyncThunk(
  "content/docDecrptDownload",
  async (req: DocDownloadRequestDTO) => {
    const resp = await docApi.docDecrpytDownload(req);
    return resp;
  }
);
export const trunkContentsDownload = createAsyncThunk(
  "content/docsDownload",
  async ({
    syncIds,
    startDownload,
  }: {
    syncIds: string[];
    startDownload: any;
  }) => {
    return await docApi.docsDownload(syncIds, startDownload);
  }
);
export const trunkDocDelete = createAsyncThunk(
  "content/delete",
  async (syncIds: string[]) => {
    return await docApi.docDelete(syncIds);
  }
);
export const trunkDocDeleteNow = createAsyncThunk(
  "content/delete/now",
  async (syncIds: string[]) => {
    return await docApi.docDeleteNow(syncIds);
  }
);
export const trunkDocRestore = createAsyncThunk(
  "content/restore",
  async (syncIds: string[]) => {
    return await docApi.docRestore(syncIds);
  }
);
export const trunkDocUploadUidCreate = createAsyncThunk(
  "content/uploadUidCreate",
  async () => {
    return await docApi.docUploadUidCreate();
  }
);
export const trunkDocUpdateUid = createAsyncThunk(
  "content/uploadUidCreate/update",
  async (uid: string) => {
    return uid;
  }
);
export const trunkDocUploadFile = createAsyncThunk(
  "content/uploadFile",
  async ({
    uid,
    file,
    setProgressPercent,
  }: {
    uid: string;
    file: FileWithPath;
    setProgressPercent?: any;
  }) => {
    return await docApi.docUploadFile(uid, file, setProgressPercent);
  }
);
export const trunkDocChangeName = createAsyncThunk(
  "content/docname",
  async (req: { contentName: string; syncId: string }) => {
    const result = await docApi.changeDocName(req);

    return result;
  }
);
export const trunkChangeDocMaster = createAsyncThunk(
  "contents/master/change",
  async ({
    syncId,
    fromUserId,
    toUserId,
    rev,
  }: {
    syncId: string | string[];
    fromUserId: string;
    toUserId: string;
    rev: number;
  }) => {
    return await docApi.changeDocMaster(syncId, fromUserId, toUserId, rev);
  }
);
export const trunkGetMemos = createAsyncThunk(
  "content/docs",
  async (req: MemoDTO) => {
    const res = await docApi.getMemos(req);
    return res;
  }
);
export const trunkDocReqRead = createAsyncThunk(
  "content/request/read",
  async (req: ReadReqeustDto) => {
    return await docApi.docRequestRead(req);
  }
);
export const trunkRequestReadList = createAsyncThunk(
  "contents/request/read",
  async ({ syncId }: { syncId: string }) => {
    return await docApi.requestLists(syncId);
  }
);
export const trunkDeleteReadRequest = createAsyncThunk(
  "contents/request/delete",
  async ({ syncId, requestId }: { syncId: string; requestId: string }) => {
    await docApi.requestReadDelete(syncId, requestId);
    return await docApi.requestLists(syncId);
  }
);
export const trunkContentCheckout = createAsyncThunk(
  "content/checkout",
  async (req: { syncId: string; revisionNo: number; workgroupId: string }) => {
    return await docApi.checkoutManualDoc(req);
  }
);
export const trunkContentUndoCheckout = createAsyncThunk(
  "content/undocheckout",
  async (req: { syncId: string; revisionNo: number; workgroupId: string }) => {
    return await docApi.undocheckoutManualDoc(req);
  }
);
export const trunkCreateFolder = createAsyncThunk(
  "folder/create",
  async (folder: FileFolderReq) => {
    return await docApi.createFolder(folder);
  }
);
export const trunkUpdateFolder = createAsyncThunk(
  "folder/update",
  async (folder: FileFolderReq) => {
    return await docApi.updateFolder(folder);
  }
);
export const trunkDeleteFolder = createAsyncThunk(
  "folder/delete",
  async (folder: FileFolderReq) => {
    return await docApi.deleteFolder(folder);
  }
);
export const trunkDocDeliveryUid = createAsyncThunk(
  "content/deliveryUid",
  async (req: DeliveryUIDRequestRespondDTO) => {
    return await docApi.docUploadFileDelivery(req);
  }
);
export const trunkDocUploadFileComplete = createAsyncThunk(
  "content/uploadFile/complete",
  async ({
    uid,
    req,
    isLoading,
  }: {
    uid: string;
    req: UploadCompleteRequestDTO;
    isLoading?: boolean;
  }) => {
    const uuid = uid === "" ? await docApi.docUploadUidCreate() : uid;
    return await docApi.docUploadFileComplete(uuid, req, isLoading);
  }
);
export const trunkDocUploadFileCompleteDelivery = createAsyncThunk(
  "content/uploadFile/complete/delivery",
  async ({ uid, req }: { uid: string; req: UploadCompleteRequestDTO }) => {
    const uuid = uid === "" ? await docApi.docUploadUidCreate() : uid;
    return await docApi.docUploadFileCompleteDelivery(uuid, req);
  }
);
export const trunkSetAddUploadProgressFiles = createAsyncThunk(
  "content/progress/add",
  async (uploadFile: UploadFile) => {
    return uploadFile;
  }
);
export const trunkSetUpdateUploadProgressFiles = createAsyncThunk(
  "content/progress/update",
  async (uploadFile: UploadFile) => {
    return uploadFile;
  }
);
export const trunkforceWebCheckin = createAsyncThunk(
  "content/forceWebcheckin",
  async ({ syncId }: { syncId: string }) => {
    return await docApi.forceWebCheckin(syncId);
  }
);
export const trunkRemoveFileFromUidList = createAsyncThunk(
  "content/uploadFile/remove",
  async ({
    uid,
    uuid,
    fileId,
  }: {
    uid: string;
    uuid: string;
    fileId: string;
  }) => {
    return await docApi.docRemoveFileFromUid(uid, fileId);
  }
);
export const trunkGetDocSyncInfo = createAsyncThunk(
  "content/getDocSyncInfo",
  async (req: { workgroupId: string; syncId: string }) => {
    return await docApi.getDocSyncInfo(req);
  }
);
export const trunkWebCheckin = createAsyncThunk(
  "content/webcheckin",
  async ({
    workgroupId,
    syncId,
    revisionNo,
  }: {
    workgroupId: string;
    syncId: string;
    revisionNo: number;
  }) => {
    return await docApi.webCheckin(workgroupId, syncId, revisionNo);
  }
);
export const trunkDoChatWithAI = createAsyncThunk(
  "aiChat/chat",
  async (request: DocAIChatRequest) => {
    return await docApi.doAIDocChat(request);
  }
);
export const trunkCancelChatWithAI = createAsyncThunk(
  "aiChat/cancel",
  async (request: DocAIChatRequest) => {
    return await docApi.cancelAIDocChat(request);
  }
);
export const trunkCheckAIDocPermission = createAsyncThunk(
  "aiChat/checkPermission",
  async (syncIds: string[]) => {
    return await docApi.checkAIDocPermission(syncIds);
  }
);
export const trunkCloseChatWithAI = createAsyncThunk(
  "aiChat/close",
  async (convoId: string) => {
    return await docApi.closeAIDocChat(convoId);
  }
);
export const trunkCheckAnswerWithAI = createAsyncThunk(
  "aiChat/check-answer",
  async (request: DocAIChatRequest) => {
    return await docApi.checkAnswerDocChat(request);
  }
);
export const trunkChangeDragItem = createAsyncThunk(
  "aiChat/addDoc",
  async (req: { request: DocInfo[]; isDirect?: boolean }) => {
    return req;
  }
);
export const trunkFolderInfo = createAsyncThunk(
  "folder/folderInfo",
  async (folderId: string) => {
    return await docApi.getFolderInfo(folderId);
  }
);
export const trunkUpdateFileFolder = createAsyncThunk(
  "folder/fileFolderUpdate",
  async (folder: FileFolderReq) => {
    return await docApi.updateFileFolder(folder);
  }
);
export const trunkDocUploadRevisionFile = createAsyncThunk(
  "content/uploadFile",
  async ({
    uid,
    file,
    syncId,
    setProgressPercent,
  }: {
    uid: string;
    file: FileWithPath;
    syncId: string;
    setProgressPercent: any;
  }) => {
    return await docApi.docUploadRevisionFile(
      uid,
      file,
      syncId,
      setProgressPercent
    );
  }
);
export const trunkGetUploadDocsProgress = createAsyncThunk(
  "content/uploadFile/getProgress",
  async (uid: string) => {
    return await docApi.getUploadDocsProgress(uid);
  }
);
export const trunkGetDocTimeline = createAsyncThunk(
  "content/timeline",
  async (req: { workgroupId: string; syncId: string }) => {
    return await docApi.getDocTimeline(req);
  }
);
export const trunketDocUserViewCount = createAsyncThunk(
  "content/permission/viewcount",
  async (reqDto: { syncId: string; workgroupId: string }) => {
    return await docApi.getDocUserViewCount(reqDto);
  }
);
export const trunkRemoveUid = createAsyncThunk(
  "content/uploadFile/removeUID",
  async () => {
    return true;
  }
);
export const trunkRecallGetContentList = createAsyncThunk(
  "content/recall",
  async () => {
    return true;
  }
);
const documentSlice = createSlice({
  name: "document",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(trunkGetContentDetail.fulfilled, (state, req) => {
      if (req.meta.arg.type === "aside") {
        state.contentAsideDetail = req.payload;
      }
      state.contentDetail = req.payload;
    });
    builder.addCase(
      trunkRecallGetContentList.fulfilled,
      (state, { payload: data }) => {
        state.recallNum += 1;
      }
    );
    builder
      .addCase(trunkGetContentList.pending, (state, resp) => {
        if (resp.meta.arg?.type === "root_search") return;
        if (resp?.meta.arg.page === 0) {
          state.docList = undefined;
          state.docListInfo = undefined;
        }
      })
      .addCase(trunkGetContentList.fulfilled, (state, resp) => {
        const data = resp.payload;
        if (resp.meta.arg?.type === "root_search") return;
        if (data?.pages?.page === 0) {
          state.docList = data.docs?.doc;
          state.docListInfo = data;
        } else if (
          data?.pages?.page > 0 &&
          state.docList &&
          data?.docs &&
          data?.docs?.count > 0
        ) {
          if (state.docList)
            state.docList = [...state.docList, ...data.docs.doc];
          if (state.docListInfo && state.docListInfo.docs)
            state.docListInfo.docs = {
              count: data.docs.count,
              doc: [...state.docListInfo.docs.doc, ...data.docs.doc],
            };
        }
      });
    builder.addCase(
      trunkDocChangeName.fulfilled,
      (state, { payload: data }) => {
        if (state.docList)
          state.docList = state.docList?.map((item) => {
            return item.syncId === data.syncId
              ? { ...item, contentName: data.contentName }
              : item;
          });
        if (state.docListInfo?.docs?.doc)
          state.docListInfo.docs.doc = state.docListInfo?.docs?.doc?.map(
            (item) => {
              return item.syncId === data.syncId
                ? { ...item, contentName: data.contentName }
                : item;
            }
          );
        if (state.contentAsideDetail?.documentInfo?.syncId === data.syncId) {
          state.contentAsideDetail.documentInfo.contentName = data.contentName;
        }
      }
    );
    builder.addCase(
      trunkUpdateFileFolder.fulfilled,
      (state, { payload: data }) => {
        if (state.docList)
          state.docList = state.docList.filter(
            (item) => !data.successSyncIds.includes(item.syncId)
          );
        if (state.docListInfo?.docs?.doc)
          state.docListInfo.docs.doc = state.docListInfo?.docs?.doc?.filter(
            (item) => !data.successSyncIds.includes(item.syncId)
          );
      }
    );
    builder.addCase(trunkCreateFolder.fulfilled, (state, arg) => {
      if (state.docListInfo?.folder?.currntFolderList)
        state.docListInfo.folder.currntFolderList = [
          { ...arg.payload, workgroupId: arg.meta.arg.workgroupId ?? "" },
          ...state.docListInfo?.folder?.currntFolderList,
        ];
    });
    builder.addCase(trunkUpdateFolder.fulfilled, (state, { payload: data }) => {
      if (state.docListInfo?.folder?.currntFolderList)
        state.docListInfo.folder.currntFolderList =
          state.docListInfo?.folder?.currntFolderList.map((item) =>
            item.folderId === data.folderId
              ? { ...item, folderName: data.folderName }
              : item
          );
    });
    builder.addCase(trunkDeleteFolder.fulfilled, (state, { payload: data }) => {
      if (state.docListInfo?.folder?.currntFolderList)
        state.docListInfo.folder.currntFolderList =
          state.docListInfo?.folder?.currntFolderList.filter(
            (item) => item.folderId !== data.folderId
          );
    });
    builder.addCase(trunkGetMemos.fulfilled, (state, arg) => {
      state.memos = arg.meta.arg.cursor
        ? {
            ...arg.payload,
            revisions: {
              count: state.memos?.revisions.count ?? 0,
              revision: state.memos?.revisions.revision
                ? [
                    ...state.memos?.revisions.revision,
                    ...arg.payload.revisions.revision,
                  ]
                : [],
            },
          }
        : arg.payload;
    });

    builder.addCase(
      trunkSaveContentThumbnail.fulfilled,
      (state, { payload: res }) => {
        state.savedContentThumbnail[res.syncId + "_" + res.revisionNo] =
          res.blob;
      }
    );
    builder.addCase(
      trunkRequestReadList.fulfilled,
      (state, { payload: res }) => {
        state.requestReadList = res;
      }
    );
    builder.addCase(
      trunkDeleteReadRequest.fulfilled,
      (state, { payload: res }) => {
        state.requestReadList = res;
      }
    );
    builder.addCase(trunkforceWebCheckin.fulfilled, (state, arg) => {
      state.docList = state.docList?.map((item) =>
        item.syncId === arg.meta.arg.syncId
          ? { ...item, concurrentUserId: null, concurrentList: undefined }
          : item
      );
      if (state.docListInfo?.docs?.doc)
        state.docListInfo.docs.doc = state.docListInfo?.docs?.doc?.map((item) =>
          item.syncId === arg.meta.arg.syncId
            ? { ...item, concurrentUserId: null, concurrentList: undefined }
            : item
        );
    });
    builder.addCase(
      trunkChangeDragItem.fulfilled,
      (state, { payload: resp }) => {
        state.dragItem = resp.request;
        state.dragDirectly = resp.isDirect ?? false;
      }
    );
    builder.addCase(
      trunkDocUploadFileCompleteDelivery.fulfilled,
      (state, arg) => {
        const res = arg.payload;
        if (!res) return;
        const user = getUserInfoFromLocalStorage() as string | "";
        const loginDto: LoginDto = JSON.parse(user);
        if (!window.location.pathname.includes("/myInbox/sent")) return;
        if (state.docListInfo?.docs?.doc)
          state.docListInfo.docs.doc = [
            ...res?.map((item: any) => {
              return {
                syncId: item.syncId,
                contentName: item.fileName,
                revisionNo: 1,
                checkoutUser: {
                  userId: "",
                  userName: "",
                },
                masterUser: {
                  userId: loginDto.userInfo.userId,
                  userName: loginDto.userInfo.userName,
                },
                checkinUser: {
                  userId: loginDto.userInfo.userId,
                  userName: loginDto.userInfo.userName,
                },
                revisionDate: item.entryTime,
                fileSize: 0,
                favoritDate: 0,
                authType: "R",
                checkin: "N",
                shareFlag: "N",
                concurrentUserId: null,
                usageCount: 0,
                deleteDate: 0,
                createUser: loginDto.userInfo.userId,
                createTime: item.entryTime,
                permissionCount: 1,
                workgroup: { workgroupId: arg.meta.arg.req.workgroupId ?? "" },
              };
            }),
            ...state.docListInfo?.docs?.doc,
          ];
      }
    );
    builder.addCase(trunkDocUploadFileComplete.fulfilled, (state, arg) => {
      const res = arg.payload;
      const user = getUserInfoFromLocalStorage() as string | "";
      const loginDto: LoginDto = JSON.parse(user);
      const currentWorkgroupId = window.location.pathname.split("/")[2] ?? "";
      if (currentWorkgroupId !== arg.meta.arg.req.workgroupId) return;
      if (!state.docListInfo?.docs) return;
      // state.docListInfo.docs.doc = Array.isArray(res)
      //   ? [
      //       ...res.map((item: any) => {
      //         const findFromProgress =
      //           state.uploadDocsProgress.uploadFiles.filter(
      //             (item1) => item1.fileId === item.fileId
      //           )[0];
      //         return {
      //           syncId: item.syncId,
      //           contentName: item.fileName,
      //           revisionNo: 1,
      //           checkoutUser: {
      //             userId: "",
      //             userName: "",
      //           },
      //           masterUser: {
      //             userId: loginDto.userInfo.userId,
      //             userName: loginDto.userInfo.userName,
      //           },
      //           checkinUser: {
      //             userId: loginDto.userInfo.userId,
      //             userName: loginDto.userInfo.userName,
      //           },
      //           revisionDate: item.entryTime,
      //           fileSize: findFromProgress?.fileSize ?? 0,
      //           favoritDate: 0,
      //           authType: "R",
      //           checkin: "N",
      //           shareFlag: "N",
      //           concurrentUserId: null,
      //           usageCount: 0,
      //           deleteDate: 0,
      //           updateTime: dayjs().valueOf(),
      //           createUser: loginDto.userInfo.userId,
      //           createTime: item.entryTime,
      //           permissionCount: 1,
      //           workgroup: {
      //             workgroupId: arg.meta.arg.req.workgroupId ?? "",
      //             workgroupName: "",
      //             desc: "",
      //             managerId: "",
      //             managerName: "",
      //             favorite: "",
      //             members: [],
      //             withdrawMembers: null,
      //             invitePolicy: 0,
      //             externalUserCount: 0,
      //             internalUserCount: 0,
      //             invitedMessage: "",
      //             status: 0,
      //             startDate: 0,
      //             endDate: 0,
      //             logDate: 0,
      //             updateDate: 0,
      //             isValidate: 0,
      //             docSize: 0,
      //             docCount: 0,
      //             readRequestCount: 0,
      //             webMeetingCount: 0,
      //             chatCount: 0,
      //           },
      //         };
      //       }),
      //       ...state.docListInfo?.docs?.doc,
      //     ]
      //   : [
      //       {
      //         ...state.docListInfo?.docs?.doc.filter(
      //           (item) => item.syncId === arg.payload.syncId
      //         )[0],
      //         revisionNo: arg.payload.revisionNo + 1,
      //         checkinUser: {
      //           userId: loginDto.userInfo.userId,
      //           userName: loginDto.userInfo.userName,
      //         },
      //         checkoutUser: {
      //           userId: "",
      //           userName: "",
      //         },
      //         revisionDate: arg.payload.entryTime,
      //         updateTime: dayjs().valueOf(),
      //       },
      //       ...state.docListInfo?.docs?.doc.filter(
      //         (item) => item.syncId !== arg.payload.syncId
      //       ),
      //     ];
      // if (!arg.meta.arg.req.syncId)
      //   state.docListInfo.total += arg.payload.length;
    });
    builder.addCase(
      trunkGetContentDownload.fulfilled,
      (state, { payload: res }) => {
        if (res.data && res.headers) {
          const file = res.data;
          const header = res.headers;
          const regex =
            /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i;
          const contentDisposition =
            header["content-disposition"].match(regex)[0];
          let fileName = contentDisposition.split("filename=");
          fileName = decodeURIComponent(fileName[1]);
          const blob = new Blob([file], { type: "application/download" });
          FileSaver.saveAs(blob, fileName);
        }
      }
    );
    builder.addCase(
      trunkContentsDownload.fulfilled,
      (state, { payload: res }) => {
        if (res.data && res.headers) {
          const file = res.data;
          const header = res.headers;
          const regex =
            /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i;
          const contentDisposition =
            header["content-disposition"].match(regex)[0];
          let fileName = contentDisposition.split("filename=");
          fileName = decodeURIComponent(fileName[1]);
          const blob = new Blob([file], { type: "application/download" });
          FileSaver.saveAs(blob, fileName);
        }
      }
    );
    builder.addCase(
      trunkDocDecryptDownload.fulfilled,
      (state, { payload: res }) => {
        if (res.data && res.headers) {
          const file = res.data;
          const header = res.headers;
          const regex =
            /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i;
          const contentDisposition =
            header["content-disposition"].match(regex)[0];
          let fileName = contentDisposition.split("filename=");
          fileName = decodeURIComponent(fileName[1]);
          const blob = new Blob([file], { type: "application/download" });
          FileSaver.saveAs(blob, fileName);
        }
      }
    );
    builder.addCase(trunkRemoveFileFromUidList.fulfilled, (state, action) => {
      state.uploadDocsProgress = {
        ...state.uploadDocsProgress,
        totalCount: state.uploadDocsProgress.totalCount - 1,
        uploadFiles: state.uploadDocsProgress.uploadFiles.filter(
          (item) => item.uuid !== action.meta.arg.uuid
        ),
      };
    });
    builder.addCase(trunkRemoveFileFromUidList.rejected, (state, action) => {
      state.uploadDocsProgress = {
        ...state.uploadDocsProgress,
        totalCount: state.uploadDocsProgress.totalCount - 1,
        uploadFiles: state.uploadDocsProgress.uploadFiles.filter(
          (item) => item.uuid !== action.meta.arg.uuid
        ),
      };
    });
    builder.addCase(trunkDocRestore.fulfilled, (state, arg) => {
      if (state.docListInfo?.docs?.doc)
        state.docListInfo.docs.doc = state.docListInfo.docs?.doc?.filter(
          (doc) => !arg.meta.arg.includes(doc.syncId)
        );
      if (state.docListInfo && state.docListInfo.docs)
        state.docListInfo = {
          ...state.docListInfo,
          total: state.docListInfo.total - 1,
          docs: {
            count: state.docListInfo.docs.count - 1,
            doc: state.docListInfo.docs?.doc?.filter(
              (doc) => !arg.meta.arg.includes(doc.syncId)
            ),
          },
        };
    });
    builder.addCase(trunkDocDelete.fulfilled, (state, { payload: result }) => {
      if (state.docListInfo?.docs?.doc)
        state.docListInfo = {
          ...state.docListInfo,
          total: state.docListInfo.total - 1,
          docs: {
            count: state.docListInfo.docs.count - 1,
            doc: state.docListInfo.docs?.doc?.filter(
              (doc) => !result.syncIds.includes(doc.syncId)
            ),
          },
        };
    });
    builder.addCase(trunkDocDeleteNow.fulfilled, (state, arg) => {
      if (state.docListInfo?.docs?.doc) {
        const remainList = state.docListInfo.docs?.doc?.filter(
          (doc) =>
            !(
              arg.meta.arg.includes(doc.syncId) &&
              !arg.payload.fails.includes(doc.syncId)
            )
        );
        state.docListInfo = {
          ...state.docListInfo,
          total: remainList.length,
          docs: {
            count: remainList.length,
            doc: remainList,
          },
        };
      }
    });
    builder.addCase(trunkChangeDocMaster.fulfilled, (state, arg) => {
      const targetSyncIds =
        typeof arg.meta.arg.syncId === "string"
          ? [arg.meta.arg.syncId]
          : arg.meta.arg.syncId;
      const targetDoc =
        state.docListInfo?.docs?.doc.filter((item) =>
          targetSyncIds.includes(item.syncId)
        )[0] ??
        (targetSyncIds.includes(
          state.contentAsideDetail?.documentInfo?.syncId ?? ""
        )
          ? state.contentAsideDetail?.documentInfo
          : undefined);
      if (!targetDoc) return;
      const member = targetDoc.workgroup.members?.filter(
        (item) => item.userId === arg.meta.arg.toUserId
      )[0];
      if (!member || !member.userName) return;
      if (state.docList) {
        state.docList = state.docList.map((item) =>
          targetSyncIds.includes(item.syncId)
            ? {
                ...item,
                master: { userId: member.userId, userName: member.userName },
                masterUser: {
                  userId: member.userId,
                  userName: member.userName,
                },
              }
            : item
        );
      }
      if (state.docListInfo?.docs) {
        state.docListInfo.docs.doc = state.docListInfo.docs?.doc?.map((item) =>
          targetSyncIds.includes(item.syncId)
            ? {
                ...item,
                master: { userId: member.userId, userName: member.userName },
                masterUser: {
                  userId: member.userId,
                  userName: member.userName,
                },
              }
            : item
        );
      }
      if (
        state.contentAsideDetail?.documentInfo &&
        targetSyncIds.includes(state.contentAsideDetail?.documentInfo.syncId)
      ) {
        state.contentAsideDetail.documentInfo.master = {
          userId: member.userId,
          userName: member.userName,
        };
        state.contentAsideDetail.documentInfo.masterUser = {
          userId: member.userId,
          userName: member.userName,
        };
      }
    });
    builder.addCase(
      trunkGetReadRequestContentList.fulfilled,
      (state, { payload: data }) => {
        state.docList = data.docs;
        if (state.docListInfo && state.docListInfo.docs) {
          state.docListInfo.total = data?.totalCount ?? 0;
          state.docListInfo.docs = {
            count: data?.totalCount ?? 0,
            doc: data.docs ?? [],
          };
        } else {
          state.docListInfo = {
            total: data.totalCount ?? 0,
            version: "1.0",
            folder: null,
            docs: { count: data?.totalCount ?? 0, doc: data.docs ?? [] },
            pages: {
              nextPage: -1,
              prevPage: -1,
              page: -1,
            },
          };
        }
        if (state.docListInfo && state.docListInfo.folder)
          state.docListInfo.folder = {
            ...state.docListInfo.folder,
            currntFolderList: [],
          };
      }
    );
    builder.addCase(trunkGetContentFavorite.fulfilled, (state, arg) => {
      if (state.docListInfo?.docs?.doc && state.docList) {
        const matchedSyncIds = state?.docList?.filter((item) =>
          arg.payload?.syncIds.includes(item.syncId)
        );
        if (arg.meta.arg.page === "favorite")
          state.docListInfo.total =
            state.docListInfo.total - matchedSyncIds.length;
        state.docListInfo.docs.doc =
          arg.meta.arg.page === "favorite"
            ? state?.docList?.filter(
                (item) => !arg.payload?.syncIds.includes(item.syncId)
              )
            : state.docList.map((item) => {
                return {
                  ...item,
                  favoritDate: arg.payload?.syncIds.includes(item.syncId)
                    ? arg.payload?.flag
                      ? 1
                      : 0
                    : item.favoritDate,
                };
              });
      }
      if (state.docList)
        state.docList =
          arg.meta.arg.page === "favorite"
            ? state?.docList?.filter(
                (item) => !arg.payload?.syncIds.includes(item.syncId)
              )
            : state?.docList?.map((item) =>
                arg.payload.syncIds.includes(item.syncId)
                  ? {
                      ...item,
                      favoritDate: arg.payload.flag ? dayjs().valueOf() : 0,
                    }
                  : item
              );
      if (
        state.contentAsideDetail?.documentInfo &&
        arg.payload.syncIds.includes(
          state.contentAsideDetail?.documentInfo?.syncId
        )
      ) {
        state.contentAsideDetail.documentInfo = {
          ...state.contentAsideDetail?.documentInfo,
          favoritDate: arg.payload.flag ? dayjs().valueOf() : 0,
        };
      }
      if (
        state.contentDetail?.documentInfo &&
        arg.payload.syncIds.includes(state.contentDetail?.documentInfo?.syncId)
      ) {
        state.contentDetail.documentInfo.favoritDate = arg.payload.flag
          ? dayjs().valueOf()
          : 0;
      }
    });
    builder.addCase(
      trunkGetDocTimeline.fulfilled,
      (state, { payload: result }) => {
        if (state.contentAsideDetail) {
          state.contentAsideDetail.timeline = result;
        }
      }
    );
    builder.addCase(trunkAddContentMemo.fulfilled, (state, req) => {
      const user = getUserInfoFromLocalStorage() as string | "";
      const loginDto: LoginDto = JSON.parse(user);
      let beforeSigns =
        state.contentAsideDetail?.documentHistory?.revisions?.revision[0]?.signs
          ?.sign ??
        state.contentDetail?.documentHistory?.revisions?.revision[0]?.signs
          ?.sign ??
        [];
      beforeSigns = [
        {
          syncId: null,
          revisionNo: 0,
          userId: loginDto.userInfo.userId,
          userName: loginDto.userInfo.userName,
          memo: req.meta.arg.memo,
          signDate: dayjs().valueOf(),
          userType: loginDto.ecoUserInfo.userType,
        },
        ...beforeSigns,
      ];
      if (state.contentAsideDetail)
        state.contentAsideDetail.documentHistory.revisions.revision[0].signs.sign =
          beforeSigns;
      if (state.contentDetail)
        state.contentDetail.documentHistory.revisions.revision[0].signs.sign =
          beforeSigns;
      if (state.docListInfo?.docs?.doc) {
        state.docListInfo.docs.doc = state.docListInfo.docs?.doc.map((item) =>
          item.syncId === req.meta.arg.syncId
            ? { ...item, signCount: (item?.signCount ?? 0) + 1 }
            : item
        );
      }
      if (state.memos) {
        const user = getUserInfoFromLocalStorage() as string | "";
        const loginDto: LoginDto = JSON.parse(user);
        state.memos.revisions.revision[0].signs.sign = [
          {
            syncId: req.meta.arg.syncId,
            revisionNo: 0,
            userId: loginDto.userInfo.userId,
            userName: loginDto.userInfo.userName,
            memo: req.meta.arg.memo,
            signDate: dayjs().valueOf(),
            userType: loginDto.ecoUserInfo.userType,
          },
          ...(state.memos.revisions.revision[0].signs.sign ?? []),
        ];
      }
    });
    builder.addCase(
      trunkDocUploadUidCreate.fulfilled,
      (state, { payload: uid }) => {
        state.uid = uid;
      }
    );
    builder.addCase(trunkDocUpdateUid.fulfilled, (state, { payload: uid }) => {
      state.uid = uid;
    });
    builder.addCase(trunkFolderInfo.fulfilled, (state, { payload: folder }) => {
      if (
        state.folderList &&
        state.folderList[0].workgroupId !== folder.folderInfo.workgroupId
      )
        state.folderList = undefined;
      state.folderList = (state.folderList ?? [folder.folderInfo]).map((item) =>
        item.folderId === folder.folderInfo.folderId
          ? {
              ...item,
              childrenFolder: folder.currntFolderList,
              parentFolder: folder.parentFolderList,
            }
          : item
      );
      const existingIds = new Set(state.folderList.map((f) => f.folderId));
      const newFolders = folder.currntFolderList
        .filter((item) => !existingIds.has(item.folderId))
        .map((item) => ({
          ...item,
          parentFolder: [...folder.parentFolderList, folder.folderInfo],
        }));
      state.folderList = state.folderList.concat(newFolders);
    });
    builder.addCase(
      trunkSetAddUploadProgressFiles.fulfilled,
      (state, { payload: uploadFile }) => {
        state.uploadDocsProgress.isMultiple = uploadFile.isMultiple;
        state.uploadDocsProgress.totalCount =
          state.uploadDocsProgress.totalCount + 1;
        state.uploadDocsProgress.totalSize =
          state.uploadDocsProgress.totalSize + (uploadFile?.fileSize ?? 0);
        state.uploadDocsProgress.uploadFiles.push(uploadFile);
      }
    );
    builder.addCase(trunkGetUploadDocsProgress.fulfilled, (state, action) => {
      const uuid = nanoid();
      state.uploadDocsProgress = {
        ...state.uploadDocsProgress,
        totalCount: action.payload.fileInfoList.length ?? 0,
        uploadFiles: action.payload.fileInfoList.map((file: any, i: number) => {
          return { ...file, uuid: uuid + i };
        }),
      };
    });
    builder.addCase(
      trunkSetUpdateUploadProgressFiles.fulfilled,
      (state, { payload: uploadFile }) => {
        if (uploadFile.uuid) {
          state.uploadDocsProgress.uploadFiles.forEach((item, index) => {
            if (item.uuid === uploadFile.uuid) {
              const objIndex = state.uploadDocsProgress.uploadFiles.findIndex(
                (obj) => obj.uuid === item.uuid
              );
              state.uploadDocsProgress.uploadFiles[objIndex] = uploadFile;
              if (uploadFile.status === "SUCCESS") {
                state.uploadDocsProgress.successCount =
                  state.uploadDocsProgress.successCount + 1;
              }
              if (uploadFile.status === "FAIL") {
                state.uploadDocsProgress.failCount =
                  state.uploadDocsProgress.failCount + 1;
              }
            }
          });
        } else {
          state.uploadDocsProgress = {
            totalCount: 0,
            totalSize: 0,
            successCount: 0,
            failCount: 0,
            progress: 0,
            fileName: "",
            uploadFiles: [],
          };
        }
      }
    );
    builder.addCase(trunkRemoveUid.fulfilled, (state, { payload: res }) => {
      state.uid = undefined;
    });
    builder.addCase(
      trunketDocUserViewCount.fulfilled,
      (state, { payload: res }) => {
        if (!state.contentDetail) return;
        // // 특별한 개별 permission 있는 애들 모집
        // const userWithUserPolicy = state.contentDetail.permissionPolicyList
        //   .filter((user) => user.targetType === "USER")
        //   .map((user1) => user1.targetValue);
        // // 워크그룹 permission 구분
        // const workgroupPermission =
        //   state.contentDetail?.permissionPolicyList?.filter(
        //     (item) => item.targetType === "WORKGROUP"
        //   )[0];
        // console.log(userWithUserPolicy, res);
        // const addedpermission = res
        //   ?.filter((user) => !userWithUserPolicy.includes(user.userId))
        //   ?.map((user1) => {
        //     return {
        //       ...workgroupPermission,
        //       targetValue: user1.userId,
        //       policy: {
        //         ...workgroupPermission?.policy,
        //         allowViewCount: user1.allowViewCount,
        //         currentViewCount: user1.currentViewCount,
        //       },
        //     };
        //   });
        // state.contentDetail = {
        //   ...state.contentDetail,
        //   permissionPolicyList: [
        //     ...state.contentDetail.permissionPolicyList,
        //     ...addedpermission,
        //   ],
        // };
      }
    );
  },
});
export const docActions = documentSlice.actions;

export default documentSlice.reducer;
